import Avatar from '@mui/material/Avatar';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Popover from '@mui/material/Popover';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { darken, lighten, rgbToHex } from '@mui/material/styles';
import useTheme from '@mui/material/styles/useTheme';
import { type CSSProperties, Fragment, type ReactNode, useState } from 'react';
import sFarbschema from '../_json/s_farbschema';
import Iconify from '../components/iconify/component';
import { type IconifyProps } from '../components/iconify/types';
import { type CustomTheme } from '../provider/ThemeProvider';
import { getFarbeFirma } from './Session';
import { checkUndefinedNullOrEmpty, checkUndefinedOrNull, generateRandomId } from './Tools';

const randomId = generateRandomId();

type BoxMaterialProps = {
    actionsAtBottom?: boolean;
    avatar?: string;
    bodyStyle?: any;
    callbackSettings?: {
        abortBtn?: { l?: string; disabled?: boolean; callback?: () => void };
        addBtn?: { l?: string; disabled?: boolean; callback?: () => void };
        deleteBtn?: { l?: string; disabled?: boolean; callback?: () => void };
        editBtn?: { l?: string; disabled?: boolean; callback?: () => void };
        saveBtn?: { l?: string; disabled?: boolean; callback?: () => void };
    };
    childActions?: ReactNode;
    childActionsOnlyAtCollapse?: boolean;
    childCollapse?: any;
    children?: any;
    collapseBtnText?: string;
    color?: string;
    customButtons?: ReactNode;
    customCardHeader?: ReactNode;
    customValue?: ReactNode;
    danger?: boolean;
    desc?: string;
    edit?: boolean;
    embed?: boolean;
    expandColor?: string;
    h?: string | any;
    hGraph?: any;
    hIcon?: IconifyProps;
    h_click?: () => void;
    info?: boolean;
    invertedColor?: boolean;
    lead?: boolean;
    link?: string;
    loading?: boolean;
    showCollapseInitially?: boolean;
    style?: any;
    success?: boolean;
    testid?: string;
    value?: number | { tooltip?: string; v?: number } | any;
    valueHeading?: ReactNode;
    warning?: boolean;
};

const BoxMaterial = (moduleProps: BoxMaterialProps) => {
    const props = {
        actionsAtBottom: false,
        callbackSettings: {},
        childActionsOnlyAtCollapse: false,
        collapseBtnText: '',
        danger: false,
        embed: false,
        info: false,
        invertedColor: false,
        loading: false,
        showCollapseInitially: false,
        success: false,
        warning: false,
        ...moduleProps,
    };
    const makeId = () => {
        let result = '';
        const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
        for (let i = 0; i < 12; i++) result += characters.charAt(Math.floor(Math.random() * characters.length));
        return result;
    };

    const [id] = useState<any>(props.desc && makeId());
    const [showCollapse, setShowCollapse] = useState<any>(props.showCollapseInitially);
    const [showInfoTooltip, setShowInfoTooltip] = useState<boolean>(false);
    const theme: CustomTheme = useTheme();
    const descBtnId = props.desc ? 'descBtn_' + id : '';
    const callbackSettings = {
        abortBtn: {},
        addBtn: {},
        deleteBtn: {},
        editBtn: {},
        saveBtn: {},
        ...props.callbackSettings,
    };

    const colorObj = sFarbschema[getFarbeFirma()];
    // 53900, 53970, 53980, 53990, 54000, 54010, 54020
    let color = undefined;
    let colorLighter = undefined;
    let colorDarker = undefined;
    if (props.color === 'dashboard') {
        color = colorObj.type === 'baseColor' ? colorObj.color : colorObj.dashboard;
        colorLighter = rgbToHex(lighten(color, 0.1));
        colorDarker = rgbToHex(darken(color, 0.1));
    } else if (props.color === 'leads') {
        color = colorObj.type === 'baseColor' ? lighten(colorObj.color, 0.25) : colorObj.leads;
        colorLighter = rgbToHex(lighten(color, 0.1));
        colorDarker = rgbToHex(darken(color, 0.1));
    } else if (props.color === 'fahrzeuge') {
        color = colorObj.type === 'baseColor' ? darken(colorObj.color, 0.25) : colorObj.fahrzeuge;
        colorLighter = rgbToHex(lighten(color, 0.1));
        colorDarker = rgbToHex(darken(color, 0.1));
    } else if (props.color === 'kunden') {
        color = colorObj.type === 'baseColor' ? lighten(colorObj.color, 0.4) : colorObj.kunden;
        colorLighter = rgbToHex(lighten(color, 0.1));
        colorDarker = rgbToHex(darken(color, 0.1));
    } else if (props.color === 'qr') {
        color = colorObj.type === 'baseColor' ? darken(colorObj.color, 0.5) : colorObj.qr;
        colorLighter = rgbToHex(lighten(color, 0.1));
        colorDarker = rgbToHex(darken(color, 0.1));
    }

    const primaryLighter = props.danger
        ? theme?.palette?.error?.lighter
        : props.warning
            ? theme?.palette?.warning?.lighter
            : props.success
                ? theme?.palette?.success?.lighter
                : props.info
                    ? theme?.palette?.info?.lighter
                    : color
                        ? colorLighter
                        : theme?.palette?.primary?.lighter;

    const primaryDarker = props.danger
        ? theme?.palette?.error?.darker
        : props.warning
            ? theme?.palette?.warning?.darker
            : props.success
                ? theme?.palette?.success?.darker
                : props.info
                    ? theme?.palette?.info?.darker
                    : color
                        ? colorDarker
                        : theme?.palette?.primary?.darker;

    const loadingGradient = 'linear-gradient(60deg, #ddd, #999)';

    const invertedGradient = 'linear-gradient(60deg, ' + theme?.palette?.background?.paper + ', ' + theme?.palette?.background?.default + ')';
    const primaryGradient = 'linear-gradient(60deg, ' + primaryLighter + ', ' + primaryDarker + ')';

    const cardStyle: CSSProperties = {
        background: props.invertedColor ? primaryGradient : undefined,
        cursor: props.h_click && props.lead ? 'pointer' : undefined,
        marginTop: ((!props.lead && !checkUndefinedOrNull(props.h)) || !checkUndefinedOrNull(props.hIcon) || !checkUndefinedOrNull(props.hGraph)) && !props.embed ? 35 : 0,
        marginBottom: props.lead ? (props.hIcon ? 10 : 5) : !props.embed ? 15 : 0,
        opacity: props.loading ? 0.35 : undefined,
        overflow: 'visible',
        ...props.style,
    };
    const cardHeaderStyle: CSSProperties = {
        display: 'flex',
        justifyContent: props.customCardHeader || props.h || props.hIcon ? 'space-between' : 'flex-end',
        alignItems: 'flex-start',
        padding: props.customCardHeader ? '0 15px 0 0' : '8px 15px',
        minHeight: 53,
    };
    const cardHeaderTextStyle: CSSProperties = {
        background: props.loading ? loadingGradient : props.invertedColor ? invertedGradient : primaryGradient,
        cursor: props.h_click && !props.lead ? 'pointer' : undefined,
        color: props.hGraph ? undefined : '#fff',
        height: props.loading ? 54 : 'fit-content',
        width: props.loading ? '8em' : props.hGraph ? '100%' : undefined,
        marginTop: -25,
        padding: props.loading || props.avatar ? 0 : 15,
    };
    const cardTopActionsStyle: CSSProperties = {
        textAlign: !checkUndefinedOrNull(props.value) || !checkUndefinedOrNull(props.customValue) ? 'right' : undefined,
        display: checkUndefinedOrNull(props.value) && checkUndefinedOrNull(props.customValue) ? 'flex' : undefined,
        alignItems: checkUndefinedOrNull(props.value) && checkUndefinedOrNull(props.customValue) ? 'center' : undefined,
        marginTop: props.customCardHeader ? 10 : undefined,
    };
    const cardBodyStyle: CSSProperties = {
        transition: !showCollapse ? 'padding 50ms ease-in-out 300ms' : 'padding 50ms ease-in-out 0ms',
        height: 'inherit',
        padding:
            !props.children && props.childCollapse
                ? !showCollapse
                    ? 0
                    : '8px 15px 15px 15px'
                : props.lead && !props.value && !props.customValue
                    ? 10
                    : props.children && !props.childCollapse
                        ? '8px 15px 15px 15px'
                        : '8px 15px 15px 15px',
        ...props.bodyStyle,
    };
    const cardActionsStyle: CSSProperties = { borderTop: '1px solid ' + theme?.palette?.divider, display: 'flex', justifyContent: 'space-between', padding: '8px 15px' };

    const fixedCardActions = [
        /* Custom Buttons */
        props.customButtons ? (
            props.loading ? (
                <Skeleton key={'boxMaterial_' + randomId + '_loadingCustom'} animation="wave" style={{ display: 'inline-block', margin: '0 5px' }} variant="rectangular" width="2em" height="2em" />
            ) : (
                props.customButtons
            )
        ) : undefined,
        /* Link anzeigen und öffnen in neuem Tab */
        props.desc ? (
            props.loading ? (
                <Skeleton key={'boxMaterial_' + randomId + '_loadingDesc'} animation="wave" style={{ display: 'inline-block', margin: '0 5px' }} variant="rectangular" width="2em" height="2em" />
            ) : (
                <Fragment key={'boxMaterial_' + randomId + '_desc'}>
                    <Tooltip title="Info anzeigen">
                        <span>
                            <IconButton id={descBtnId} data-testid="desc" color="primary" onClick={() => setShowInfoTooltip(true)} size="large">
                                <Iconify icon="mdi:information" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Popover
                        open={showInfoTooltip}
                        onClose={() => setShowInfoTooltip(false)}
                        anchorEl={document.getElementById(descBtnId)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        PaperProps={{ style: { maxWidth: 400, padding: 10, overflow: 'visible' } }}
                    >
                        {props.desc}
                    </Popover>
                </Fragment>
            )
        ) : undefined,
        /* Link anzeigen und öffnen in neuem Tab */
        props.link ? (
            props.loading ? (
                <Skeleton key={'boxMaterial_' + randomId + '_loadingLink'} animation="wave" style={{ display: 'inline-block', margin: '0 5px' }} variant="rectangular" width="2em" height="2em" />
            ) : (
                <Tooltip key={'boxMaterial_' + randomId + '_link'} title="Öffnen">
                    <span>
                        <IconButton data-testid="link" color="primary" onClick={() => window.open(props.link, '_blank')} size="large">
                            <Iconify icon="mdi:open-in-new" />
                        </IconButton>
                    </span>
                </Tooltip>
            )
        ) : undefined,
        /* Ändern, Speichern, verwerfen */
        props.edit != undefined ? (
            props.edit ? (
                <Stack key={'boxMaterial_' + randomId + '_divSaveAbort'} direction="row">
                    {!checkUndefinedOrNull(callbackSettings?.abortBtn?.callback) ? (
                        props.loading ? (
                            <Skeleton animation="wave" style={{ display: 'inline-block', margin: '0 5px' }} variant="rectangular" width="2em" height="2em" />
                        ) : (
                            <Tooltip title={callbackSettings?.abortBtn?.l || 'Änderungen verwerfen'}>
                                <span>
                                    <IconButton
                                        data-testid="abortBtn"
                                        color="primary"
                                        disabled={callbackSettings?.abortBtn?.disabled}
                                        aria-label="Änderungen verwerfen"
                                        onClick={callbackSettings?.abortBtn?.callback}
                                        size="large"
                                    >
                                        <Iconify icon="mdi:close" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )
                    ) : undefined}
                    {!checkUndefinedOrNull(callbackSettings?.saveBtn?.callback) ? (
                        props.loading ? (
                            <Skeleton animation="wave" style={{ display: 'inline-block', margin: '0 5px' }} variant="rectangular" width="2em" height="2em" />
                        ) : (
                            <Tooltip title={callbackSettings?.saveBtn?.l || 'Speichern'}>
                                <span>
                                    <IconButton
                                        data-testid="saveBtn"
                                        color="primary"
                                        disabled={callbackSettings?.saveBtn?.disabled}
                                        aria-label="Speichern"
                                        onClick={callbackSettings?.saveBtn?.callback}
                                        size="large"
                                    >
                                        <Iconify icon="mdi:content-save" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )
                    ) : undefined}
                </Stack>
            ) : !checkUndefinedOrNull(callbackSettings?.editBtn?.callback) ? (
                props.loading ? (
                    <Skeleton key={'boxMaterial_' + randomId + '_loadingEdit'} animation="wave" style={{ display: 'inline-block', margin: '0 5px' }} variant="rectangular" width="2em" height="2em" />
                ) : (
                    <Tooltip key={'boxMaterial_' + randomId + '_editBtn'} className="noPrint" title={callbackSettings?.editBtn?.l || 'Bearbeiten'}>
                        <span>
                            <IconButton
                                data-testid="editBtn"
                                color="primary"
                                disabled={callbackSettings?.editBtn?.disabled}
                                aria-label="Ändern"
                                onClick={callbackSettings?.editBtn?.callback}
                                size="large"
                            >
                                <Iconify icon="mdi:pencil" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )
            ) : undefined
        ) : undefined,
        /* Ändern, Speichern, verwerfen */
        !checkUndefinedOrNull(callbackSettings?.deleteBtn?.callback) ? (
            !props.edit ? (
                props.loading ? (
                    <Skeleton animation="wave" style={{ display: 'inline-block', margin: '0 5px' }} variant="rectangular" width="2em" height="2em" />
                ) : (
                    <Tooltip title={callbackSettings?.deleteBtn?.l || 'Löschen'}>
                        <span>
                            <IconButton
                                data-testid="deleteBtn"
                                color="primary"
                                disabled={callbackSettings?.deleteBtn?.disabled}
                                aria-label="Löschen"
                                onClick={callbackSettings?.deleteBtn?.callback}
                                size="large"
                            >
                                <Iconify icon="mdi:delete" />
                            </IconButton>
                        </span>
                    </Tooltip>
                )
            ) : undefined
        ) : undefined,
        /* Element hinzufügen */
        !checkUndefinedOrNull(callbackSettings?.addBtn?.callback) ? (
            props.loading ? (
                <Skeleton key={'boxMaterial_' + randomId + '_loadingPlus'} animation="wave" style={{ display: 'inline-block', margin: '0 5px' }} variant="rectangular" width="2em" height="2em" />
            ) : (
                <Tooltip key={'boxMaterial_' + randomId + '_plus'} className="noPrint" title={callbackSettings?.addBtn?.l || 'Hinzufügen'}>
                    <span>
                        <IconButton
                            data-testid="addBtn"
                            color="primary"
                            disabled={callbackSettings?.addBtn?.disabled}
                            aria-label="Hinzufügen"
                            onClick={callbackSettings?.addBtn?.callback}
                            size="large"
                        >
                            <Iconify icon="mdi:plus" />
                        </IconButton>
                    </span>
                </Tooltip>
            )
        ) : undefined,
        /* Collapse inkl. Button-Text */
        props.childCollapse ? (
            <div key={'boxMaterial_' + randomId + '_divCollapse'} style={{ display: 'flex', alignItems: 'center' }}>
                {!checkUndefinedNullOrEmpty(props.collapseBtnText) ? (
                    props.loading ? (
                        <Skeleton animation="wave" variant="rectangular" width="5em" height="1.5em" />
                    ) : (
                        <span onClick={() => setShowCollapse(!showCollapse)} style={{ cursor: 'pointer', color: theme.palette.text.secondary }}>
                            {props.collapseBtnText}
                        </span>
                    )
                ) : undefined}
                {props.loading ? (
                    <Skeleton animation="wave" style={{ display: 'inline-block', margin: '0 5px' }} variant="rectangular" width="2em" height="2em" />
                ) : (
                    <Tooltip title="Mehr anzeigen">
                        <span>
                            <IconButton
                                style={{
                                    color: props.expandColor ? props.expandColor : undefined,
                                    padding: 5,
                                    transform: 'rotate(' + (showCollapse ? 180 : 0) + 'deg)',
                                    transition: theme.transitions.create('transform', {
                                        duration: theme.transitions.duration.shortest,
                                    }),
                                    marginLeft: 3,
                                }}
                                color="primary"
                                aria-expanded={showCollapse}
                                aria-label="Mehr anzeigen"
                                onClick={() => setShowCollapse(!showCollapse)}
                                size="large"
                            >
                                <div data-testid="collapse" style={{ lineHeight: 1 }}>
                                    <Iconify icon="mdi:chevron-down" />
                                </div>
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </div>
        ) : undefined,
    ];

    return (
        <Card
            key={'boxMaterial_' + randomId}
            elevation={props.embed ? 0 : 1}
            data-testid={props.testid}
            onClick={props.lead && !props.value && !props.customValue ? props.h_click : undefined}
            style={cardStyle}
        >
            {!checkUndefinedOrNull(props.customCardHeader) ||
                !checkUndefinedOrNull(props.h) ||
                !checkUndefinedOrNull(props.hIcon) ||
                !checkUndefinedOrNull(props.desc) ||
                !checkUndefinedOrNull(props.value) ||
                !checkUndefinedOrNull(props.customValue) ||
                !checkUndefinedOrNull(props.valueHeading) ||
                !checkUndefinedOrNull(props.hGraph) ||
                (!props.actionsAtBottom &&
                    (!checkUndefinedOrNull(props.link) ||
                        !checkUndefinedOrNull(props.edit) ||
                        !checkUndefinedOrNull(props.callbackSettings?.addBtn?.callback) ||
                        !checkUndefinedOrNull(props.childCollapse))) ? (
                /* CardHeader */
                <div key={'boxMaterial_' + randomId + '_cardHeader'} data-testid="cardHeader" style={cardHeaderStyle}>
                    {props.customCardHeader ? props.customCardHeader : undefined}
                    {props.hGraph || props.h || props.hIcon || props.avatar ? (
                        <Paper key={'boxMaterial_' + randomId + '_cardHeaderText'} style={cardHeaderTextStyle} onClick={props.h_click}>
                            {props.hGraph ? (
                                props.hGraph
                            ) : props.loading ? (
                                <Skeleton animation="wave" variant="rectangular" width="100%" height="100%" />
                            ) : props.avatar ? (
                                <Avatar style={{ backgroundColor: 'transparent', color: '#fff', height: 59, width: 56 }} src={props.avatar} variant="rounded">
                                    {props.hIcon ? <Iconify icon={props.hIcon} sx={{ marginRight: props.h ? 2 : 0 }} /> : undefined}
                                </Avatar>
                            ) : (
                                <h4
                                    className="cardTitle"
                                    style={{ fontWeight: '400', fontSize: '1em', display: 'flex', alignItems: 'center', margin: !checkUndefinedOrNull(props.hIcon) ? 0 : '4.5px 0' }}
                                >
                                    {props.hIcon ? <Iconify icon={props.hIcon} sx={{ marginRight: props.h ? 2 : 0 }} /> : undefined}
                                    {props.h ? <span>{props.h}</span> : undefined}
                                </h4>
                            )}
                        </Paper>
                    ) : undefined}

                    {!checkUndefinedOrNull(props.value) || !checkUndefinedOrNull(props.customValue) || props.lead ? (
                        <div style={cardTopActionsStyle}>
                            {/* CardBoxMaterial / LeadBoxMaterial Content */}
                            {props.loading ? (
                                <Skeleton animation="wave" height="1.75em" width="6em" />
                            ) : (
                                <h5
                                    onClick={props.h_click}
                                    style={{
                                        fontWeight: 300,
                                        color: theme?.palette?.text?.secondary,
                                        margin: 0,
                                        fontSize: '1.2em',
                                        cursor: !checkUndefinedOrNull(props.h_click) ? 'pointer' : undefined,
                                    }}
                                >
                                    {props.valueHeading}
                                </h5>
                            )}
                            {props.loading ? (
                                <Skeleton animation="wave" height="3em" width="4em" style={{ marginLeft: 'auto' }} />
                            ) : Array.isArray(props.value) ? (
                                <p
                                    onClick={props.h_click}
                                    style={{
                                        fontWeight: 300,
                                        color: theme?.palette?.text?.primary,
                                        marginTop: 6,
                                        marginBottom: 0,
                                        fontSize: '2em',
                                        cursor: !checkUndefinedOrNull(props.h_click) ? 'pointer' : undefined,
                                    }}
                                >
                                    {props.value.map((value, index) => {
                                        if (index < props.value.length - 1) {
                                            return (
                                                <span key={'spanValue_' + index}>
                                                    <Tooltip
                                                        title={props.value[index].tooltip || ''}
                                                        disableFocusListener={!props.value[index].tooltip}
                                                        disableHoverListener={!props.value[index].tooltip}
                                                        disableTouchListener={!props.value[index].tooltip}
                                                    >
                                                        <span
                                                            onClick={props.h_click}
                                                            style={{
                                                                fontWeight: 300,
                                                                color: !checkUndefinedNullOrEmpty(index)
                                                                    ? theme?.palette &&
                                                                    theme?.palette[index === 2 ? 'success' : index === 1 ? 'warning' : index === 0 ? 'error' : 'primary'][
                                                                    theme?.palette?.mode === 'dark' ? 'lighter' : 'main'
                                                                    ]
                                                                    : theme?.palette?.text?.primary,
                                                                marginTop: 6,
                                                                marginBottom: 0,
                                                                cursor: !checkUndefinedOrNull(props.h_click) ? 'pointer' : undefined,
                                                            }}
                                                        >
                                                            {typeof props.value[index] === 'object'
                                                                ? props.value[index].v !== 0 || props.lead
                                                                    ? props.value[index].v
                                                                    : 'keine'
                                                                : props.value[index] !== 0 || props.lead
                                                                    ? props.value[index]
                                                                    : 'keine'}
                                                        </span>
                                                    </Tooltip>{' '}
                                                    |{' '}
                                                </span>
                                            );
                                        } else {
                                            return (
                                                <Tooltip
                                                    key={'tooltipValue_' + index}
                                                    title={props.value[index].tooltip || ''}
                                                    disableFocusListener={!props.value[index].tooltip}
                                                    disableHoverListener={!props.value[index].tooltip}
                                                    disableTouchListener={!props.value[index].tooltip}
                                                >
                                                    <span
                                                        onClick={props.h_click}
                                                        style={{
                                                            fontWeight: 300,
                                                            color: !checkUndefinedNullOrEmpty(index)
                                                                ? theme?.palette &&
                                                                theme?.palette[index === 2 ? 'success' : index === 1 ? 'warning' : index === 0 ? 'error' : 'primary'][
                                                                theme?.palette?.mode === 'dark' ? 'lighter' : 'main'
                                                                ]
                                                                : theme?.palette?.text?.primary,
                                                            marginTop: 6,
                                                            marginBottom: 0,
                                                            cursor: !checkUndefinedOrNull(props.h_click) ? 'pointer' : undefined,
                                                        }}
                                                    >
                                                        {typeof props.value[index] === 'object'
                                                            ? props.value[index].v !== 0 || props.lead
                                                                ? props.value[index].v
                                                                : 'keine'
                                                            : props.value[index] !== 0 || props.lead
                                                                ? props.value[index]
                                                                : 'keine'}
                                                    </span>
                                                </Tooltip>
                                            );
                                        }
                                    })}
                                </p>
                            ) : (
                                <Tooltip
                                    title={(props.value && props.value.tooltip) || ''}
                                    disableFocusListener={!props.value || !props.value.tooltip}
                                    disableHoverListener={!props.value || !props.value.tooltip}
                                    disableTouchListener={!props.value || !props.value.tooltip}
                                >
                                    <p
                                        onClick={props.h_click}
                                        style={{
                                            fontWeight: 300,
                                            color: theme?.palette?.text?.primary,
                                            marginTop: 6,
                                            marginBottom: 0,
                                            fontSize: '2em',
                                            cursor: !checkUndefinedOrNull(props.h_click) ? 'pointer' : undefined,
                                        }}
                                    >
                                        {!checkUndefinedOrNull(props.customValue)
                                            ? props.customValue
                                            : typeof props.value === 'object'
                                                ? props.value.v !== 0 || props.lead
                                                    ? props.value.v
                                                    : 'keine'
                                                : props.value !== 0 || props.lead
                                                    ? props.value
                                                    : 'keine'}
                                    </p>
                                </Tooltip>
                            )}
                        </div>
                    ) : undefined}

                    {!props.actionsAtBottom &&
                        (!checkUndefinedOrNull(props.desc) ||
                            !checkUndefinedOrNull(props.link) ||
                            !checkUndefinedOrNull(props.edit) ||
                            !checkUndefinedOrNull(props.callbackSettings?.addBtn?.callback) ||
                            !checkUndefinedOrNull(props.childCollapse) ||
                            !checkUndefinedOrNull(props.customButtons)) ? (
                        <div key={'boxMaterial_' + randomId + '_cardTopActions'} style={cardTopActionsStyle}>
                            {fixedCardActions}
                        </div>
                    ) : undefined}
                </div>
            ) : undefined}
            {(!checkUndefinedOrNull(props.value) || !checkUndefinedOrNull(props.customValue) || props.lead) && !props.loading && !checkUndefinedOrNull(props.children) ? (
                <Divider variant="fullWidth" />
            ) : undefined}
            {!checkUndefinedOrNull(props.children) || !checkUndefinedOrNull(props.childCollapse) ? (
                <CardContent key={'boxMaterial_' + randomId + '_cardContent'} data-testid="cardContent" style={cardBodyStyle}>
                    {!props.loading ? (
                        <>
                            {/* Children */}
                            <div key={'boxMaterial_' + randomId + '_content'}>{props.children}</div>
                            {/* Collapse-Inhalt */}
                            {!checkUndefinedOrNull(props.childCollapse) ? (
                                <Collapse key={'boxMaterial_' + randomId + '_collapse'} in={showCollapse} timeout="auto" unmountOnExit>
                                    {props.childCollapse}
                                </Collapse>
                            ) : undefined}
                        </>
                    ) : (
                        <>
                            <Skeleton key={'boxMaterial_' + randomId + '_loading1'} animation="wave" variant="rectangular" style={{ marginBottom: 5 }} width="40%" height="1.5em" />
                            <Skeleton key={'boxMaterial_' + randomId + '_loading2'} animation="wave" variant="rectangular" style={{ marginBottom: 5 }} width="55%" height="1.5em" />
                            <Skeleton key={'boxMaterial_' + randomId + '_loading3'} animation="wave" variant="rectangular" style={{ marginBottom: 5 }} width="70%" height="1.5em" />
                            <Skeleton key={'boxMaterial_' + randomId + '_loading4'} animation="wave" variant="rectangular" style={{ marginBottom: 5 }} width="65%" height="1.5em" />
                            <Skeleton key={'boxMaterial_' + randomId + '_loading5'} animation="wave" variant="rectangular" width="30%" height="1.5em" />
                        </>
                    )}
                </CardContent>
            ) : undefined}
            {(props.childActions && ((props.childActionsOnlyAtCollapse && showCollapse) || !props.childActionsOnlyAtCollapse)) || props.actionsAtBottom ? (
                <CardActions style={cardActionsStyle}>
                    <div style={{ display: 'flex' }}>
                        {props.childActions && props.loading ? (
                            <>
                                <Skeleton key={'boxMaterial_' + randomId + '_loading1a'} animation="wave" variant="rectangular" style={{ marginRight: 10 }} width={86} height={33} />
                                <Skeleton key={'boxMaterial_' + randomId + '_loading2a'} animation="wave" variant="rectangular" style={{ marginRight: 10 }} width={86} height={33} />
                                <Skeleton key={'boxMaterial_' + randomId + '_loading3a'} animation="wave" variant="rectangular" style={{ marginRight: 10 }} width={86} height={33} />
                            </>
                        ) : (
                            props.childActions
                        )}
                    </div>

                    {props.actionsAtBottom &&
                        (!checkUndefinedOrNull(props.desc) ||
                            !checkUndefinedOrNull(props.link) ||
                            !checkUndefinedOrNull(props.edit) ||
                            !checkUndefinedOrNull(props.callbackSettings?.addBtn?.callback) ||
                            !checkUndefinedOrNull(props.childCollapse)) ? (
                        <div style={cardTopActionsStyle}>{fixedCardActions}</div>
                    ) : undefined}
                </CardActions>
            ) : undefined}
        </Card>
    );
};

export default BoxMaterial;
